import type { ClipboardEvent, FormEvent } from 'react';

import { use } from 'react';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import z from 'zod';

import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';

import { ModalInstanceContext } from '../../../contexts/ModalContext';

import { useAppForm } from '../../../form-hooks/form';
import { fetchBrowserInfo } from '../helpers/helpers';

export type SupportIssueType = {
  content: string;
  metadata: string;
  files: File[];
};

const ACCEPTED_FILE_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];

interface SupportIssueModalContentProps {
  /**
   * Form submit callback
   */
  onSubmit: (value: SupportIssueType) => Promise<boolean>;
  /**
   * SupportIssueModalContent test id
   */
  testId?: string;
}

const SupportIssueModalContent = ({
  onSubmit,
  testId,
}: SupportIssueModalContentProps) => {
  const { t } = useTranslation();
  const modalInstance = use(ModalInstanceContext);

  const initialValues: SupportIssueType = {
    content: '',
    metadata: JSON.stringify(fetchBrowserInfo()),
    files: [],
  };

  const validationSchema = z.object({
    content: z
      .string()
      .min(50, t('SupportIssueModal.Form.MinContentLength'))
      .max(2000, t('SupportIssueModal.Form.MaxContentLength')),
    metadata: z.string(),
    files: z.array(z.file()),
  });

  const form = useAppForm({
    defaultValues: initialValues,
    onSubmit: async ({ value }) => {
      const ok = await onSubmit(value);
      if (ok) {
        modalInstance.resolve?.();
      }
    },
    validators: {
      onSubmit: validationSchema,
      onChange: validationSchema,
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  const addFiles = (files: File[]) => {
    const acceptedFiles = files.filter((file) =>
      ACCEPTED_FILE_TYPES.includes(file.type),
    );

    if (acceptedFiles.length !== files.length) {
      toast.error(t('SupportIssueModal.Form.InvalidFileType'));
    }

    acceptedFiles.forEach((file) => {
      form.pushFieldValue('files', file);
    });
  };

  const handleContentPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedFiles = Array.from(e.clipboardData.items)
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile())
      .filter((file): file is File => file !== null);

    if (!pastedFiles.length) return;

    e.preventDefault();
    addFiles(pastedFiles);
  };

  return (
    <div className="w-full h-full" {...getTestProps(testId, 'content')}>
      <p className="text-lg whitespace-pre-line">
        <Trans
          i18nKey="SupportIssueModal.Paragraph"
          components={{
            1: <strong />,
          }}
        />
      </p>
      <form.AppForm>
        <form className="h-full" onSubmit={handleSubmit} noValidate>
          <div className="my-8">
            <form.AppField name="content">
              {(field) => (
                <field.Textarea
                  additionalTextareaClasses="placeholder:text-slate-900 dark:placeholder:text-white"
                  placeholderText={t(
                    'SupportIssueModal.Form.MessagePlaceholder',
                  )}
                  label={t('SupportIssueModal.Form.Message')}
                  required
                  onPaste={handleContentPaste}
                  {...getTestProps(testId, 'content-input', 'testId')}
                />
              )}
            </form.AppField>
            <p className="mt-6 text-lg">
              {t('SupportIssueModal.Form.Paragraph')}
            </p>
            <form.AppField name="files">
              {(field) => (
                <field.FilesSelectorField
                  testId={testId}
                  dropFileHandler={addFiles}
                  multiple={true}
                  dndLabel={t('SupportIssueModal.Form.AttachmentsRestrictions')}
                  accept={ACCEPTED_FILE_TYPES.join(',')}
                />
              )}
            </form.AppField>
          </div>
          <div
            className="absolute w-full left-0 bottom-0 p-6 flex justify-center items-center gap-2 bg-white
          border-t dark:bg-slate-900 dark:border-slate-700 rounded-b-md"
          >
            <form.Subscribe>
              {(state) => (
                <Button
                  type="submit"
                  disabled={!state.canSubmit || !state.isTouched}
                  {...getTestProps(testId, 'submit-button', 'testId')}
                >
                  {t('SupportIssueModal.Form.SubmitButton')}
                </Button>
              )}
            </form.Subscribe>
            <Button
              buttonColor="blueBordered"
              onClick={() => modalInstance.resolve?.()}
              {...getTestProps(testId, 'cancel-button', 'testId')}
            >
              {t('SupportIssueModal.Form.Cancel')}
            </Button>
          </div>
        </form>
      </form.AppForm>
    </div>
  );
};

export default SupportIssueModalContent;
